import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { ChartLineUp, Camera, FilePdf, TrendUp, TrendDown, Minus } from "@phosphor-icons/react";

const VERDICT_META = {
    migliorato: { label: "Migliorato", cls: "bg-emerald-50 text-emerald-700 border-emerald-300", Icon: TrendUp },
    stabile: { label: "Stabile", cls: "bg-amber-50 text-amber-700 border-amber-300", Icon: Minus },
    peggiorato: { label: "Peggiorato", cls: "bg-rose-50 text-rose-700 border-rose-300", Icon: TrendDown },
    "—": { label: "—", cls: "bg-gray-100 text-gray-500 border-gray-300", Icon: Minus },
};

const fmtVal = (v, kind) => (v === null || v === undefined ? "—" : kind === "pct" ? `${v}%` : v);
const fmtDate = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");

function VerdictBadge({ verdict, testid }) {
    const m = VERDICT_META[verdict] || VERDICT_META["—"];
    const { Icon } = m;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${m.cls}`} data-testid={testid}>
            <Icon size={11} weight="bold" /> {m.label}
        </span>
    );
}

function DeltaCell({ m }) {
    const d = m.delta_abs;
    if (d === null || d === undefined) return <span className="text-gray-400">—</span>;
    const positive = d > 0;
    const sign = positive ? "+" : "";
    const unit = m.kind === "pct" ? " pt" : "";
    const good = m.verdict === "migliorato";
    const bad = m.verdict === "peggiorato";
    const color = good ? "text-emerald-600" : bad ? "text-rose-600" : "text-gray-500";
    return (
        <span className={`font-bold tabular-nums ${color}`}>
            {sign}{d}{unit}
            {m.delta_pct !== null && m.delta_pct !== undefined && (
                <span className="ml-1 text-[10px] font-normal text-gray-400">({sign}{m.delta_pct}%)</span>
            )}
        </span>
    );
}

function TrendWindow({ label, w, testid }) {
    return (
        <div className="border border-gray-200 bg-white p-4" data-testid={testid}>
            <div className="overline mb-3">{label}</div>
            <div className="grid grid-cols-3 gap-3 text-center">
                <div><div className="font-display-black text-xl tabular-nums">{w?.runs ?? 0}</div><div className="overline">Run</div></div>
                <div><div className="font-display-black text-xl tabular-nums">{w?.perizia_found ?? 0}</div><div className="overline">Perizie</div></div>
                <div><div className="font-display-black text-xl tabular-nums text-indigo-600">{w?.deep_ready ?? 0}</div><div className="overline">Deep ready</div></div>
                <div><div className="font-display-black text-xl tabular-nums">{w?.text_extracted ?? 0}</div><div className="overline">Estratte</div></div>
                <div><div className="font-display-black text-xl tabular-nums text-rose-600">{w?.http_429 ?? 0}</div><div className="overline">429</div></div>
                <div><div className="font-display-black text-xl tabular-nums">{w?.failed ?? 0}</div><div className="overline">Fallite</div></div>
            </div>
        </div>
    );
}

export function KpiComparison() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [baselineId, setBaselineId] = useState("");
    const [label, setLabel] = useState("");
    const [capturing, setCapturing] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const load = useCallback(async (bid) => {
        try {
            const res = await api.get("/admin/kpi/compare", { params: bid ? { baseline_id: bid } : {} });
            setData(res.data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(""); }, [load]);

    const onSelectBaseline = (e) => {
        const bid = e.target.value;
        setBaselineId(bid);
        load(bid);
    };

    const captureSnapshot = async () => {
        setCapturing(true);
        try {
            await api.post("/admin/kpi/snapshot", null, { params: { label } });
            toast.success("Snapshot KPI catturato");
            setLabel("");
            await load(baselineId);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setCapturing(false);
        }
    };

    const downloadPdf = async () => {
        setDownloading(true);
        try {
            const res = await api.get("/admin/kpi/compare/export.pdf", {
                params: baselineId ? { baseline_id: baselineId } : {},
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `kpi-confronto-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download PDF non riuscito");
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return <div className="border border-gray-200 bg-white p-5 mb-8 text-sm text-gray-500" data-testid="kpi-comparison-loading">Caricamento confronto KPI…</div>;
    }
    if (!data) return null;

    const snaps = data.snapshots_available || [];
    const baseline = data.baseline;
    const overall = data.overall_verdict;
    const trend = data.trend || {};

    return (
        <div className="mb-8" data-testid="kpi-comparison">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <ChartLineUp size={16} weight="bold" />
                    <h2 className="font-display-black text-lg">Confronto KPI — pre/post scheduler</h2>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        type="text"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Etichetta snapshot (opzionale)"
                        data-testid="kpi-snapshot-label-input"
                        className="border border-gray-300 h-9 px-3 text-sm focus:border-black outline-none w-full sm:w-56"
                    />
                    <button
                        onClick={captureSnapshot}
                        disabled={capturing}
                        data-testid="kpi-capture-snapshot-btn"
                        className="bg-black text-white hover:bg-gray-800 h-9 px-3 text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
                    >
                        <Camera size={14} weight="bold" /> {capturing ? "Cattura…" : "Cattura snapshot"}
                    </button>
                </div>
            </div>

            {/* Baseline selector + overall verdict + PDF */}
            <div className="border border-gray-200 bg-white p-5 mb-4" data-testid="kpi-comparison-header">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-center gap-3 flex-wrap w-full md:w-auto">
                        <div className="w-full sm:w-auto">
                            <div className="overline mb-1">Baseline a confronto</div>
                            <select
                                value={baselineId}
                                onChange={onSelectBaseline}
                                data-testid="kpi-baseline-select"
                                className="border border-gray-300 h-9 px-2 text-sm focus:border-black outline-none w-full sm:w-auto sm:min-w-[260px]"
                            >
                                <option value="">{snaps.length ? "Più vecchio (default)" : "Nessuno snapshot"}</option>
                                {snaps.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.label} · {fmtDate(s.created_at)}
                                    </option>
                                ))}
                            </select>
                            {baseline && (
                                <div className="text-xs text-gray-400 mt-1" data-testid="kpi-baseline-meta">
                                    Catturata il {fmtDate(baseline.created_at)} da {baseline.created_by_email || "—"}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-right">
                            <div className="overline mb-1">Valutazione complessiva</div>
                            <VerdictBadge verdict={overall} testid="kpi-overall-verdict" />
                        </div>
                        <button
                            onClick={downloadPdf}
                            disabled={downloading}
                            data-testid="kpi-download-pdf-btn"
                            className="border border-gray-300 hover:border-black h-9 px-3 text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                        >
                            <FilePdf size={15} weight="bold" /> {downloading ? "Generazione…" : "Scarica PDF"}
                        </button>
                    </div>
                </div>
                {!baseline && (
                    <div className="mt-3 text-sm text-gray-500" data-testid="kpi-no-baseline-hint">
                        Cattura il primo snapshot per fissare la baseline "pre-scheduler" e iniziare a misurare la crescita del dataset.
                    </div>
                )}
            </div>

            {/* KPI comparison table */}
            <div className="border border-gray-200 bg-white mb-4 overflow-x-auto" data-testid="kpi-metrics-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                            <th className="px-4 py-3 font-bold">KPI</th>
                            <th className="px-3 py-3 font-bold text-right">Baseline</th>
                            <th className="px-3 py-3 font-bold text-right">Attuale</th>
                            <th className="px-3 py-3 font-bold text-right">Variazione</th>
                            <th className="px-4 py-3 font-bold">Valutazione</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.metrics.map((m) => (
                            <tr key={m.key} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`kpi-row-${m.key}`}>
                                <td className="px-4 py-3 font-semibold">{m.label}</td>
                                <td className="px-3 py-3 text-right tabular-nums text-gray-500">{fmtVal(m.baseline, m.kind)}</td>
                                <td className="px-3 py-3 text-right tabular-nums font-bold">{fmtVal(m.current, m.kind)}</td>
                                <td className="px-3 py-3 text-right"><DeltaCell m={m} /></td>
                                <td className="px-4 py-3"><VerdictBadge verdict={m.verdict} testid={`kpi-verdict-${m.key}`} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Trend windows */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kpi-trend">
                <TrendWindow label="Oggi" w={trend.today} testid="kpi-trend-today" />
                <TrendWindow label="Ultimi 7 giorni" w={trend.last_7d} testid="kpi-trend-7d" />
                <TrendWindow label="Ultimi 30 giorni" w={trend.last_30d} testid="kpi-trend-30d" />
            </div>
        </div>
    );
}
